<template>
  <div class="section-header">
    <div class="section-header__left">
      <slot name="back" />
      <i v-if="icon" class="fa-regular section-header__icon" :class="icon"></i>
      <slot name="title">
        <span v-if="title" class="section-header__title">{{ title }}</span>
      </slot>
    </div>
    <div class="section-header__actions">
      <slot />
    </div>
  </div>
</template>

<script>
export default {
  props: {
    icon:  {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
  },
};
</script>

<style lang="scss" scoped>
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  // Wrap driven by content, not by a viewport breakpoint: as soon as the
  // title and the actions can't share a line, the actions move to their own
  // row. This holds for any number of buttons and any container width,
  // including narrow columns on a wide screen.
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 15px;
  margin-bottom: 15px;

  &__left {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    // The basis is the title's "don't squeeze me below this" width — once the
    // actions would push it narrower, the row wraps instead.
    flex: 1 1 260px;
  }

  &__icon {
    color: #6c757d;
    font-size: 1.2em;
    flex-shrink: 0;
  }

  &__title {
    font-size: 1.5em;
    line-height: 1.4;
    font-weight: 600;
    text-transform: none;
    color: #444;

    // Let long titles wrap onto new lines instead of forcing the row wider
    // than its container (which caused horizontal page scroll next to the
    // action button). `min-width: 0` lets this flex child shrink so wrapping
    // can kick in.
    //
    // `break-word`, not `anywhere`: wrap between words, and split a word only
    // when it genuinely cannot fit a line on its own. `anywhere` let the
    // browser hyphenate mid-word at any squeeze ("Strip / e / Setti / ngs").
    min-width: 0;
    overflow-wrap: break-word;
  }

  &__title::first-letter {
    text-transform: uppercase;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    // Buttons stack onto a second row rather than overflowing the container
    // when there are several of them (Stripe Settings ships four).
    //
    // The box must be allowed to narrow for that: with `flex-shrink: 0` it
    // keeps its max-content width, overflows the header, and its own
    // `flex-wrap` never gets a chance to engage. Buttons are `nowrap`, so
    // shrinking wraps them onto new lines instead of squashing them.
    flex-wrap: wrap;
    min-width: 0;
    // Keeps the group right-aligned once it wraps onto a line of its own,
    // where `justify-content: space-between` no longer has a second item
    // to push against.
    margin-left: auto;
  }

  @media (max-width: 480px) {
    &__icon {
      font-size: 1em;
    }

    &__title {
      font-size: 1.2em;
    }

    &__actions {
      width: 100%;
      justify-content: flex-end;
    }
  }
}

// Bootstrap's default bottom margin on buttons/labels throws off flex
// centering here (the extra margin is part of the flex item's box, so
// centering the box shifts the visible button up).
:deep(.btn) {
  margin: 0 !important;
}
</style>
